import * as React from 'react';
import { ISecurity, IMachineryType } from '@models';
import { EditRestriction } from './EditRestriction';

import {
    useTranslation,
    useSearch,
    useDataTable,
    useDialogs,
} from '@components';


interface IProps {
    workId: number,
    removeRestrictedMachinery: Function,
    handleToast: (message: string, success: boolean) => void,
    editRestriction: Function,
    security: ISecurity
    machineryTypes: IMachineryType[];
    filters?: any //TODO: Define a proper type for filters
    searchTerm?: string | null;
}

interface IResult {
    id: number,
    machineryType: number
    machineryTypeName: string
    name: string
    prefixLicenseNumber: string
    licenseNumber: string
    suffixLicenseNumber: string
    code: string
    contractorName: string
    applicantName: string
    applicantEmail: string
    date: string
    motive: string
    remarks: string
    isActive: boolean
    deactivatedAt?: string
    workId: number
}

export function RestrictedMachineries(props: IProps) {

    const { t } = useTranslation();
    const dialogs = useDialogs();
    const canEditRestriction = props.security.hasPermission('edit.restriction', false);

    const machineries = useSearch<IResult>({
        workId: props.workId,
        search: 'machineries/restricted',
        filters: {
            workId: props.workId,
            searchTerm: props.searchTerm,
            ...props.filters
        },
    });

    React.useEffect(() => {
        machineries.doSearch();
    }, []);

    React.useEffect(() => {
        machineries.doSearch();
    }, [props.searchTerm, props.filters]);

    const handleRemove = async (workId: number, id: number,) => {
        const resp = await props.removeRestrictedMachinery(workId, id);

        if (resp.isError === false) {
            props.handleToast(t('Machinery has been unrestricted'), true);
            machineries.doSearch();
        }
        else {
            props.handleToast(t('Error while unrestricting machinery'), false);
        }
    }

    const getActions = () => {
        return [
            {
                title: t('Unrestrict'),
                disabled: (row: IResult) => !row.isActive,
                icon: 'fa fa-unlock',
                onClick: (row: IResult) => {
                    handleRemove(row.workId, row.id);
                }
            },
            {
                title: t(canEditRestriction ? 'Edit' : 'View data'),
                icon: canEditRestriction ? 'fas fa-edit' : 'fas fa-eye',
                disabled: (row: IResult) => !row.isActive,
                onClick: (row: IResult) => {
                    dialogs.show('edit', { row: row })
                }
            },
            {
                title: t('View data'),
                icon: 'fas fa-eye',
                disabled: (row: IResult) => row.isActive,
                onClick: (row: IResult) => {
                    dialogs.show('edit', { row: row })
                }
            }
        ];
    }

    const showDate = (date: string) => {
        return new Date(date).toLocaleDateString();
    }

    const dataTable = useDataTable({
        className: ``,
        tooltip: true,
        columns:
            [
                {
                    title: t('Date'),
                    render: d => <span title={showDate(d.date)}>{showDate(d.date)}</span>,
                    field: 'date',
                    className: 'td-md',
                    sortKey: 'date',
                },
                {
                    title: t('In effect'),
                    render: d => <span title={`${d.isActive ? t('Yes') : showDate(d.deactivatedAt!)}`}>{`${d.isActive ? t('Yes') : showDate(d.deactivatedAt!)}`}</span>,
                    field: 'isActive',
                    className: 'td-md',
                    sortKey: 'isActive',
                },
                {
                    title: t('machinery.model'),
                    field: 'name',
                    className: 'td-md left',
                    sortKey: 'name',
                },
                {
                    title: t('Machinery type'),
                    field: 'machineryTypeName',
                    className: 'td-md left',
                    sortKey: 'machineryTypeName',
                },
                {
                    title: t('License number'),
                    render: d => <span title={`${d.prefixLicenseNumber ?? ''}${d.licenseNumber}${d.suffixLicenseNumber ?? ''}`}>{`${d.prefixLicenseNumber ?? ''}${d.licenseNumber}${d.suffixLicenseNumber ?? ''}`}</span>,
                    field: 'licenseNumber',
                    className: 'td-md left',
                    sortKey: 'licenseNumber',
                },
                {
                    title: t('machinery.codes'),
                    field: 'code',
                    className: 'td-md left',
                    sortKey: 'code',
                },
                {
                    title: t('Contractor'),
                    field: 'contractorName',
                    className: 'td-md left',
                    sortKey: 'contractorName',
                },
                {
                    title: t('Applicant'),
                    field: 'applicantName',
                    className: 'td-md left',
                    sortKey: 'applicantName',
                },
                {
                    title: t('Applicant email'),
                    field: 'applicantEmail',
                    className: 'td-md left',
                    sortKey: 'applicantEmail',
                },
                {
                    title: t('Motive'),
                    field: 'reason',
                    className: 'td-md left',
                    sortKey: 'code',
                },
                {
                    title: t('Remarks'),
                    field: 'remarks',
                    className: 'td-md left',
                    sortKey: 'code',
                },
            ],
        actions: [
            {
                headerClassName: 'td-sm',
                actions: getActions()
            }
        ],
        data: machineries.value,
    });

    const onEditRestriction = () => {
        dialogs.hide('edit');
        machineries.doSearch();
        props.handleToast(t('Restriction has been edited successfully'), true);
    }

    return (
        <div>
            {dialogs.render('edit', {
                        title: canEditRestriction ? t('Edit') : t('View data'),
                        className: 'g pd',
                        style: { width: '60vw' }
                    }, (r: any) =>
                        <EditRestriction
                            oldValues={r.row}
                            workId={props.workId}
                            editRestriction={props.editRestriction}
                            handleClose={onEditRestriction}
                            handleToast={props.handleToast}
                            canEditRestriction={canEditRestriction && r.row.isActive}
                            machineryTypes={props.machineryTypes}
                            />
                    )}
            {dataTable()}
        </div>
    );

}
